// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file Code.tsx
 * @input Uses React, StyleX, theme tokens
 * @output Exports Code component for inline code styling
 * @position Core implementation; lives in own Code/ dir, re-exported by CodeBlock/
 *
 * SYNC: When modified, update:
 * - /packages/core/src/CodeBlock/index.ts (exports if types change)
 * - /packages/cli/templates/blocks/components/Code/ (showcase blocks)
 * - /packages/cli/templates/blocks/components/CodeBlock/ (showcase blocks)
 */
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, spacingVars, radiusVars, typographyVars, typeScaleVars } from "../theme/tokens.stylex.js";
import { mergeProps } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { jsx as _jsx } from "react/jsx-runtime";
const styles = {
  base: {
    kMv6JI: "x9m5x89",
    kGuDYH: "xp03k98",
    kLWn49: "x15bjb6t",
    kWkggS: "xwmxj5m",
    kg3NbH: "x7a5moj",
    k8WAf4: "x1hp7d8q",
    kaIpWk: "xx3sua9",
    kHjlTd: "x1mzt3pk",
    kTgw9: "x13faqbe",
    $$css: true
  }
};
const colorStyles = {
  primary: {
    kMwMTN: "x1tgivj0",
    $$css: true
  },
  secondary: {
    kMwMTN: "xv1l7n4",
    $$css: true
  },
  inherit: {
    kMwMTN: "x1heor9g",
    $$css: true
  }
};
const sizeStyles = {
  inherit: {
    kGuDYH: "x1qlqyl8",
    kLWn49: "x15bjb6t",
    $$css: true
  }
};

/** Text color for {@link Code}, mirroring the primary/secondary/inherit subset of Text. */

/** Font size for {@link Code}. `'inherit'` adopts the surrounding text size. */

/**
 * Inline code element. Renders a styled `<code>` with monospace font,
 * muted background, and design-system-consistent sizing.
 *
 * For fenced code blocks with syntax highlighting, use `CodeBlock`.
 *
 * @example
 * ```
 * <Text type="body">
 *   Use <Code>const x = 1</Code> to declare a variable.
 * </Text>
 * ```
 */
export function Code({
  children,
  color = 'primary',
  size,
  xstyle,
  className,
  style,
  ref,
  ...props
}) {
  return /*#__PURE__*/_jsx("code", {
    ref: ref,
    ...props,
    ...mergeProps(themeProps('code', {
      color
    }), stylex.props(styles.base, colorStyles[color], size && sizeStyles[size], xstyle), className, style),
    children: children
  });
}
Code.displayName = 'Code';